import Vue from 'vue'
import store from './vuex'
import { getI18nInstance } from './vue-i18n'
import './axios'
import './rollbar'
import buildClient from '@/api/client'
import LazyLoadDirective from '@/directives/lazyload'
import DataTestIdDirective from '@/directives/data-test-id'
import MultiRefDirective from '@/directives/multi-ref'
import { registerStores } from '@/helpers/stores'
import to from 'await-to-js'

Vue.directive('lazyload', LazyLoadDirective)
Vue.directive('data-test-id', DataTestIdDirective)
Vue.directive('multi-ref', MultiRefDirective)

const defaultOptions = {
  render: null, // Function to render the component: h => h(MyComponent)
  mount: null, // Selector to mount the vue instance in
  apiQueries: [],
  /* Type: Array of objects
   * Queries to execute before creating the vue instance:
   * {
   *   file: 'filename-of-query',
   *   args: [], // array of arguments if any
   *   store: 'where-to-store-it',
   * }
   * -> Will get the query from "@/api/filename-of-query.js"
   * -> Will store the result in "@/stores/where-to-store-it.js"
   */
  stores: [], // Stores to create that are not part of "apiQueries"
}

async function newVue(params, callback) {
  const options = { ...defaultOptions, ...params }

  const { data, storeSettings } = await runApiQueries(options.apiQueries)

  const vueOptions = {
    render: options.render,
    router: options.router,
    i18n: getI18nInstance(),
  }
  options.stores.forEach(s => (storeSettings[s] = storeSettings[s] || []))
  await updateVuexStore(data, storeSettings)
  vueOptions.store = store

  const vm = new Vue(vueOptions)
  vm.$mount(options.mount)
  if (callback) callback(vm)

  return vm
}

async function getApiQuery(filename, args = []) {
  const [err, imported] = await to(import(/* webpackMode: "eager" */ `@/api/${filename}`))
  if (err) throw `Unable to find the file '@/api/${filename}' specified in 'apiQueries' setting.`

  const getQuery = imported.default
  return getQuery(...args)
    .replace(/^[\s\r\n]*(query ?)?\{/, '')
    .replace(/\}[\s\r\n]*$/, '')
}

async function runApiQueries(apiQueries) {
  const queries = []
  const storeSettings = {}

  await Promise.all(
    apiQueries.map(async obj => {
      const query = await getApiQuery(obj.file, obj.args)
      const queryReference = query.match(/\w+/)[0]
      queries.push(query)

      storeSettings[obj.store] = storeSettings[obj.store] || []
      storeSettings[obj.store].push(queryReference)
    })
  )
  if (queries.length < 1) {
    return new Promise(resolve => resolve({ data: {}, storeSettings }))
  } else {
    return await buildClient()
      .query(`{${queries.join('')}}`)
      .then(data => {
        return { data, storeSettings }
      })
  }
}

async function updateVuexStore(data, storeSettings) {
  const stores = {}

  for (let storeKey in storeSettings) {
    const [err, imported] = await to(import(/* webpackMode: "eager" */ `@/stores/${storeKey}`))
    if (err) throw `Unable to find the file '@/stores/${storeKey}' specified in 'stores' setting.`

    stores[storeKey] = imported.default
    stores[storeKey].state = stores[storeKey].state || {}

    storeSettings[storeKey].forEach(queryReference => {
      stores[storeKey].state[queryReference] = data[queryReference]
    })
  }
  registerStores(stores)
}

export { Vue, newVue }
